<template>
  <div class="referral-page">
    <h2>我的礼券</h2>
    <p class="hint">所有用户均可创建双享礼券；臻享礼券须由管理员开通臻享资格后方可设置。每张礼券被绑定后不可再次使用。</p>
    <el-alert
      class="share-help"
      type="info"
      :closable="false"
      show-icon
      title="分享说明"
      description="注册链接给未注册用户，绑定链接给已注册用户。测评链接和课程链接会带上券号，对方打开后注册或登录时仍能绑上。"
    />

    <el-card v-if="isPartner" class="section-card" shadow="never">
      <template #header>
        <span>臻享礼券（每人一个）</span>
        <el-tag type="warning" size="small" style="margin-left: 8px">臻享资格已开通</el-tag>
      </template>
      <p class="section-hint">{{ PARTNER_COUPON_HINT }}</p>
      <p class="section-hint">{{ PARTNER_COMMISSION_SCOPE_HINT }}</p>
      <div v-if="partnerCoupon" class="current-row">
        <span class="code-text">{{ partnerCoupon.couponNo }}</span>
        <span class="name-text">{{ partnerCoupon.displayName }}</span>
        <el-tag :type="partnerCoupon.status === 'used' ? 'info' : 'success'" size="small">
          {{ partnerCoupon.status === 'used' ? '已使用' : '未使用' }}
        </el-tag>
        <el-button link type="primary" @click="copyText(partnerCoupon.couponNo)">复制</el-button>
        <el-button link type="primary" @click="copyRegisterLink(partnerCoupon.couponNo)">注册链接</el-button>
        <el-button link type="primary" @click="copyBindLink(partnerCoupon.couponNo)">绑定链接</el-button>
        <el-button link type="primary" @click="openContentLink(partnerCoupon.couponNo)">测评或课程链接</el-button>
        <el-button link type="primary" @click="openFunnel(partnerCoupon)">漏斗</el-button>
        <el-button link type="primary" @click="showQr(partnerCoupon.couponNo, 'register')">注册二维码</el-button>
        <el-button link type="primary" @click="showQr(partnerCoupon.couponNo, 'bind')">绑定二维码</el-button>
      </div>
      <el-form v-if="partnerCanEdit" class="partner-form" @submit.prevent="savePartnerCoupon">
        <el-form-item label="券号">
          <el-input v-model="partnerForm.couponNo" maxlength="20" placeholder="4～20 位，字母开头" />
        </el-form-item>
        <el-form-item label="名称">
          <el-input v-model="partnerForm.displayName" maxlength="20" placeholder="展示名称" />
        </el-form-item>
        <el-button type="primary" :loading="partnerSaving" @click="savePartnerCoupon">保存臻享礼券</el-button>
      </el-form>
      <el-alert v-else-if="partnerCoupon?.status === 'used'" type="info" :closable="false" show-icon title="臻享礼券已被使用，无法修改" />
    </el-card>

    <el-card v-else class="section-card partner-locked" shadow="never">
      <template #header>臻享礼券</template>
      <p class="section-hint">臻享资格由管理员开通，开通后可在此设置专属臻享礼券。</p>
    </el-card>

    <el-card class="section-card" shadow="never">
      <template #header>双享礼券</template>
      <p class="section-hint">{{ NORMAL_COUPON_CREATE_HINT }}</p>
      <el-form inline @submit.prevent="createNormalCoupon">
        <el-form-item label="名称">
          <el-input v-model="createForm.displayName" maxlength="20" placeholder="如：老王专属福利" style="width: 220px" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="creating" @click="createNormalCoupon">创建双享礼券</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <div class="toolbar">
      <span v-if="slotsRemaining != null" class="slots-hint">还可创建 {{ slotsRemaining }} 张双享礼券</span>
      <el-select v-model="statusFilter" placeholder="状态" class="filter-select" clearable @change="fetchList">
        <el-option label="全部状态" value="" />
        <el-option label="可用" value="active" />
        <el-option label="已使用" value="used" />
      </el-select>
      <el-select v-model="typeFilter" placeholder="类型" class="filter-select" clearable @change="fetchList">
        <el-option label="全部类型" value="" />
        <el-option v-for="(label, value) in LIST_COUPON_TYPE_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="fetchList">刷新</el-button>
      <el-button link type="primary" @click="$router.push('/referral/earnings')">查看收益</el-button>
    </div>

    <el-table :data="coupons" v-loading="loading" style="width: 100%; margin-top: 16px">
      <el-table-column prop="displayName" label="名称" min-width="120" />
      <el-table-column prop="couponNo" label="券号" width="150">
        <template #default="scope">
          <span class="code-text">{{ scope.row.couponNo }}</span>
          <el-button link type="primary" size="small" @click="copyText(scope.row.couponNo)">复制</el-button>
          <el-button link type="primary" size="small" @click="copyRegisterLink(scope.row.couponNo)">注册</el-button>
          <el-button link type="primary" size="small" @click="copyBindLink(scope.row.couponNo)">绑定</el-button>
          <el-button link type="primary" size="small" @click="openContentLink(scope.row.couponNo)">内容</el-button>
          <el-button link type="primary" size="small" @click="openFunnel(scope.row)">漏斗</el-button>
          <el-button link type="primary" size="small" @click="showQr(scope.row.couponNo, 'register')">注册码</el-button>
        </template>
      </el-table-column>
      <el-table-column prop="couponType" label="类型" width="120">
        <template #default="scope">{{ couponTypeLabel(scope.row.couponType) }}</template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="90">
        <template #default="scope">
          <el-tag :type="scope.row.status === 'active' ? 'success' : 'info'">
            {{ statusLabel(scope.row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="绑定用户" min-width="120">
        <template #default="scope">
          <span v-if="scope.row.usedByUserId">{{ scope.row.usedByUsername || scope.row.usedByUserId }}</span>
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column label="创建时间" width="170">
        <template #default="scope">{{ formatTime(scope.row.createdAt) }}</template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="total > pageSize"
      class="pager"
      layout="prev, pager, next"
      :total="total"
      :page-size="pageSize"
      :current-page="page"
      @current-change="onPageChange"
    />
    <el-dialog v-model="qrVisible" :title="qrTitle" width="360px" destroy-on-close>
      <div class="qr-wrap">
        <vue-qrcode v-if="qrUrl" :value="qrUrl" :options="{ width: 240, margin: 2 }" tag="img" />
        <p class="qr-url">{{ qrUrl }}</p>
      </div>
    </el-dialog>
    <el-dialog v-model="funnelOpen" title="推广漏斗" width="420px">
      <p class="hint">打开从带券链接上线后才开始计。注册是绑定时账号刚建好的人。付费是绑定之后该客户的消费笔数和金额。</p>
      <ul v-loading="funnelLoading" class="funnel-list">
        <li>打开 {{ funnel.openCount }} 人</li>
        <li>注册 {{ funnel.registerCount }} 人</li>
        <li>绑定 {{ funnel.bindCount }} 人</li>
        <li>付费 {{ funnel.payCount }} 笔，¥{{ (Number(funnel.payCents || 0) / 100).toFixed(2) }}</li>
      </ul>
    </el-dialog>
    <el-dialog v-model="contentLinkOpen" title="复制带礼券的内容链接" width="480px">
      <p class="section-hint">对方从这条链接进入测评或课程，券号会带到注册和登录。</p>
      <el-radio-group v-model="contentKind" @change="loadContentOptions">
        <el-radio-button label="exam">测评</el-radio-button>
        <el-radio-button label="course">课程</el-radio-button>
      </el-radio-group>
      <el-select
        v-model="contentId"
        filterable
        remote
        clearable
        :remote-method="loadContentOptions"
        :loading="contentLoading"
        placeholder="搜索名称"
        style="width: 100%; margin-top: 12px"
      >
        <el-option v-for="item in contentOptions" :key="item.id" :label="item.name" :value="item.id" />
      </el-select>
      <div v-if="contentShareUrl" class="content-share-url">
        <span>分享链接</span>
        <el-input :model-value="contentShareUrl" readonly />
      </div>
      <template #footer>
        <el-button @click="contentLinkOpen = false">取消</el-button>
        <el-button type="primary" :disabled="!contentShareUrl" @click="copyContentLink">复制链接</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import VueQrcode from '@chenfengyuan/vue-qrcode'
import {
  COUPON_TYPE_LABELS,
  LIST_COUPON_TYPE_LABELS,
  NORMAL_COUPON_CREATE_HINT,
  PARTNER_COUPON_HINT,
  PARTNER_COMMISSION_SCOPE_HINT,
  couponTypeLabel,
  fetchPartnerReferralCoupon,
  savePartnerReferralCoupon,
  buildCouponBindUrl,
  buildRegisterWithCouponUrl,
  buildExamWithCouponUrl,
  buildCourseWithCouponUrl
} from '@/utils/referralCoupon'

export default {
  name: 'ReferralCouponsView',
  components: { VueQrcode },
  data() {
    return {
      COUPON_TYPE_LABELS,
      LIST_COUPON_TYPE_LABELS,
      NORMAL_COUPON_CREATE_HINT,
      PARTNER_COUPON_HINT,
      PARTNER_COMMISSION_SCOPE_HINT,
      loading: false,
      contentLinkOpen: false,
      funnelOpen: false,
      funnelLoading: false,
      funnel: { openCount: 0, registerCount: 0, bindCount: 0, payCount: 0, payCents: 0 },
      contentCouponNo: '',
      contentKind: 'exam',
      contentId: '',
      contentOptions: [],
      contentLoading: false,
      creating: false,
      partnerSaving: false,
      coupons: [],
      total: 0,
      page: 1,
      pageSize: 20,
      statusFilter: '',
      typeFilter: '',
      isPartner: false,
      partnerCoupon: null,
      partnerForm: { couponNo: '', displayName: '' },
      createForm: { displayName: '' },
      slotsRemaining: null,
      qrVisible: false,
      qrUrl: '',
      qrTitle: ''
    }
  },
  computed: {
    partnerCanEdit() {
      return !this.partnerCoupon || this.partnerCoupon.canEdit
    },
    contentShareUrl() {
      if (!this.contentId) return ''
      return this.contentKind === 'course'
        ? buildCourseWithCouponUrl(this.contentCouponNo, this.contentId)
        : buildExamWithCouponUrl(this.contentCouponNo, this.contentId)
    }
  },
  created() {
    if (!localStorage.getItem('jwt_token')) {
      this.$goToLogin(this.$route.fullPath)
      return
    }
    this.initPage()
  },
  methods: {
    couponTypeLabel,
    statusLabel(s) {
      if (s === 'active') return '可用'
      if (s === 'used') return '已使用'
      return s || '—'
    },
    formatTime(ts) {
      if (!ts) return '—'
      return new Date(ts * 1000).toLocaleString()
    },
    async initPage() {
      await this.loadPartnerFlag()
      if (this.isPartner) {
        await this.loadPartnerCoupon()
      }
      await this.fetchList()
    },
    async loadPartnerFlag() {
      try {
        const res = await axios.post('/api/v1/referral/earnings/summary', {})
        if (res.data.code === 200) {
          this.isPartner = !!res.data.data?.isPartner
        }
      } catch {
        this.isPartner = false
      }
    },
    async loadPartnerCoupon() {
      try {
        this.partnerCoupon = await fetchPartnerReferralCoupon()
        this.partnerForm = {
          couponNo: this.partnerCoupon?.couponNo || '',
          displayName: this.partnerCoupon?.displayName || ''
        }
      } catch (e) {
        this.$message.error(e.message || '加载臻享礼券失败')
      }
    },
    async fetchList() {
      this.loading = true
      try {
        const res = await axios.post('/api/v1/referral/coupon/my-list', {
          page: this.page,
          pageSize: this.pageSize,
          status: this.statusFilter,
          couponType: this.typeFilter
        })
        if (res.data.code !== 200) {
          this.$message.error(res.data.message || '加载失败')
          return
        }
        this.coupons = res.data.data?.coupons || []
        this.total = res.data.data?.total || 0
        if (res.data.data?.normalSlotsRemaining != null) {
          this.slotsRemaining = res.data.data.normalSlotsRemaining
        }
        this.notifyNewlyUsed(res.data.data?.coupons || [])
      } catch (e) {
        this.$message.error(e.response?.data?.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    async createNormalCoupon() {
      const name = (this.createForm.displayName || '').trim()
      if (!name) {
        this.$message.warning('请填写礼券名称')
        return
      }
      this.creating = true
      try {
        const res = await axios.post('/api/v1/referral/coupon/create', { displayName: name })
        if (res.data.code !== 200) {
          this.$message.error(res.data.message || '创建失败')
          return
        }
        this.$message.success('双享礼券已创建')
        this.createForm.displayName = ''
        this.page = 1
        await this.fetchList()
      } catch (e) {
        this.$message.error(e.response?.data?.message || '创建失败')
      } finally {
        this.creating = false
      }
    },
    async savePartnerCoupon() {
      const couponNo = (this.partnerForm.couponNo || '').trim()
      if (!couponNo) {
        this.$message.warning('请填写臻享礼券号')
        return
      }
      this.partnerSaving = true
      try {
        this.partnerCoupon = await savePartnerReferralCoupon({
          couponNo,
          displayName: (this.partnerForm.displayName || '').trim()
        })
        this.$message.success('臻享礼券已保存')
        await this.fetchList()
      } catch (e) {
        this.$message.error(e.message || '保存失败')
      } finally {
        this.partnerSaving = false
      }
    },
    copyText(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$message.success('已复制')
      }).catch(() => {
        this.$message.warning('复制失败：' + text)
      })
    },
    copyRegisterLink(couponNo) {
      const url = buildRegisterWithCouponUrl(couponNo)
      navigator.clipboard.writeText(url).then(() => {
        this.$message.success('已复制注册链接（新用户注册时自动绑券）')
      }).catch(() => {
        this.$message.warning('复制失败：' + url)
      })
    },
    copyBindLink(couponNo) {
      const url = buildCouponBindUrl(couponNo)
      navigator.clipboard.writeText(url).then(() => {
        this.$message.success('已复制绑定链接（已注册用户打开后绑券）')
      }).catch(() => {
        this.$message.warning('复制失败：' + url)
      })
    },
    async openFunnel(row) {
      this.funnelOpen = true
      this.funnelLoading = true
      this.funnel = { openCount: 0, registerCount: 0, bindCount: 0, payCount: 0, payCents: 0 }
      try {
        const res = await axios.post('/api/v1/referral/coupon/funnel', { couponId: row.id })
        const data = res.data || {}
        if (data.code !== 200) {
          this.$message.error(data.message || '读取漏斗失败')
          return
        }
        this.funnel = data.data || this.funnel
      } catch (e) {
        this.$message.error(e.message || '读取漏斗失败')
      } finally {
        this.funnelLoading = false
      }
    },
    openContentLink(couponNo) {
      this.contentCouponNo = couponNo
      this.contentId = ''
      this.contentKind = 'exam'
      this.contentLinkOpen = true
      this.loadContentOptions('')
    },
    async loadContentOptions(keyword) {
      const q = typeof keyword === 'string' ? keyword.trim() : ''
      this.contentLoading = true
      try {
        if (this.contentKind === 'course') {
          const res = await axios.post('/api/v1/knowledge/courses/guest/list', { page: 1, pageSize: 30, keyword: q })
          const list = res.data?.data?.list || []
          this.contentOptions = list.map((item) => ({ id: item.kcId, name: item.name }))
        } else {
          const res = await axios.post('/api/v1/exam/list', { page: 1, pageSize: 30, examName: q })
          const list = res.data?.data?.exams || []
          this.contentOptions = list.map((item) => ({ id: item.id, name: item.examName }))
        }
      } catch (e) {
        this.contentOptions = []
      } finally {
        this.contentLoading = false
      }
    },
    copyContentLink() {
      const url = this.contentShareUrl
      if (!url) return
      this.copyText(url)
    },
    showQr(couponNo, kind) {
      const url = kind === 'bind' ? buildCouponBindUrl(couponNo) : buildRegisterWithCouponUrl(couponNo)
      this.qrUrl = url
      this.qrTitle = kind === 'bind' ? '绑定链接二维码' : '注册链接二维码'
      this.qrVisible = true
    },
    notifyNewlyUsed(coupons) {
      const usedIds = coupons.filter((c) => c.status === 'used').map((c) => c.id).sort().join(',')
      const key = 'referral_coupon_used_snapshot'
      const prev = localStorage.getItem(key) || ''
      if (prev && usedIds !== prev) {
        const prevSet = new Set(prev.split(',').filter(Boolean))
        const newly = coupons.filter((c) => c.status === 'used' && !prevSet.has(String(c.id)))
        if (newly.length) {
          this.$message.success(`有 ${newly.length} 张礼券已被绑定使用`)
        }
      }
      localStorage.setItem(key, usedIds)
    },
    onPageChange(p) {
      this.page = p
      this.fetchList()
    }
  }
}
</script>

<style scoped>
.referral-page { max-width: 880px; width: 100%; margin: 0 auto; padding: 24px 20px 40px; box-sizing: border-box; }
.hint { color: #606266; margin-bottom: 16px; line-height: 1.6; }
.funnel-list { margin: 0; padding-left: 18px; line-height: 1.9; }
.section-card { margin-bottom: 16px; }
.section-hint { margin: 0 0 12px; color: #909399; font-size: 13px; line-height: 1.5; }
.current-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.code-text { font-family: monospace; font-weight: 600; margin-right: 4px; }
.name-text { color: #606266; }
.partner-form { max-width: 420px; }
.toolbar { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
.filter-select { width: 130px; }
.pager { margin-top: 16px; justify-content: center; }
.slots-hint { color: #909399; font-size: 13px; margin-right: 8px; }
.share-help { margin-bottom: 16px; }
.qr-wrap { text-align: center; }
.qr-url { margin-top: 12px; font-size: 12px; color: #909399; word-break: break-all; }
.content-share-url { margin-top: 12px; display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: #606266; }
</style>
